import { useMemo } from "react";
import { useGetList } from "react-admin";
import type { DeductibleHistoryItem, EndorsementListItem } from "@/types";
import type { DeductibleModificationConfig } from "@/types/endorsementConfig";
import { formatValidPeriod } from "@/resources/policies/hooks/useEndorsements";
import { formatUSD } from "@/utils/formatters/currencyFormatter";

// ─────────────────────────────────────────────────────────────
// Formatting helpers
// ─────────────────────────────────────────────────────────────

const NO_DECIMALS = { minimumFractionDigits: 0, maximumFractionDigits: 0 };

/**
 * Formats a dollar amount as a display string using the shared formatUSD utility.
 * e.g., 350000 → "$350,000"
 */
export const formatDeductibleDollars = (dollars: number): string => formatUSD(dollars, NO_DECIMALS);

/**
 * Fetches deductible modification history for a claimant, optionally filtered by policy.
 * Uses the `GET /claimants/{claimantId}/deductible-modifications` endpoint.
 *
 * Also fetches endorsements for the policy to determine:
 * - Whether any deductible_modification endorsement exists
 * - Whether all such endorsements exclude lasered claimants
 */
export const useDeductibleHistory = (claimantId?: string, policyId?: string) => {
  const { data, isLoading, refetch } = useGetList<DeductibleHistoryItem>(
    `claimants/${claimantId}/deductible-modifications`,
    {
      filter: policyId ? { policy_id_ref: policyId } : {},
      pagination: { page: 1, perPage: 100 },
      sort: { field: "trigger_date", order: "DESC" },
    },
    { enabled: !!claimantId }
  );

  // Fetch endorsements for the policy to check deductible_modification existence + config
  const { data: endorsements = [], isLoading: endorsementsLoading } = useGetList<EndorsementListItem>(
    `policies/${policyId}/endorsements`,
    {
      pagination: { page: 1, perPage: 100 },
      filter: {},
      sort: { field: "created_datetime", order: "DESC" },
    },
    { enabled: !!policyId }
  );

  const deductibleModEndorsements = useMemo(
    () => endorsements.filter((e) => e.type === "deductible_modification"),
    [endorsements]
  );

  /** True if at least one deductible_modification endorsement exists on the policy */
  const hasDeductibleModEndorsement = deductibleModEndorsements.length > 0;

  /** True if every deductible_modification endorsement excludes lasered claimants */
  const allEndorsementsExcludeLasered = useMemo(
    () =>
      hasDeductibleModEndorsement &&
      deductibleModEndorsements.every(
        (e) => (e.config as DeductibleModificationConfig | null)?.is_excludes_lasered === true
      ),
    [hasDeductibleModEndorsement, deductibleModEndorsements]
  );

  const history = data ?? [];

  /** History sorted by trigger_date descending (most recent first) */
  const sortedHistory = useMemo(
    () => [...history].sort((a, b) => (b.trigger_date ?? "").localeCompare(a.trigger_date ?? "")),
    [history]
  );

  /** Only currently active/triggered modifications */
  const activeModifications = useMemo(() => sortedHistory.filter((h) => h.is_triggered), [sortedHistory]);

  /** The current effective deductible — latest active modification's modified_deductible, or null if none */
  const currentEffectiveDeductible = useMemo(
    () => (activeModifications.length > 0 ? activeModifications[0].modified_deductible : null),
    [activeModifications]
  );

  /** The original (base) deductible from the earliest modification, or null if no history */
  const baseDeductible = useMemo(() => {
    if (sortedHistory.length === 0) return null;
    return sortedHistory[sortedHistory.length - 1].original_deductible;
  }, [sortedHistory]);

  return {
    history: sortedHistory,
    isLoading: isLoading || endorsementsLoading,
    refetch,
    activeModifications,
    currentEffectiveDeductible,
    baseDeductible,
    hasHistory: sortedHistory.length > 0,
    hasDeductibleModEndorsement,
    allEndorsementsExcludeLasered,
    formatDeductibleDollars,
    formatValidPeriod,
  };
};
